import React, { useEffect, useState } from "react";
import {
    Container,
    Typography,
    Box,
    Paper,
    Button,
    FormControl,
    RadioGroup,
    Radio,
    FormControlLabel,
    Autocomplete,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    CircularProgress,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Card,
    CardContent,
    Grid
} from "@mui/material";

import { fetchAudibleOptions, fetchAudibleSummary, postAudibleArchiveByPMIds, getAudibleArchiveHistoryMetadata } from "../../../API/audible";
import { useUserContext } from "../../../contexts/ProviderProvider";
import useNotification from "../../../hooks/useNotification";
import ArchiveProductMetadataTable from "./ArchiveProductMetadataTable";
import ArchiveHistoryTable from "./ArchiveHistoryTable";
import ArchiveHistoryMetadataModal from "./ArchiveHistoryMetadataModal";

const ArchiveComponent = () => {
    // States for search options
    const [searchType, setSearchType] = useState("university");
    const [searchOptions, setSearchOptions] = useState({ university: [], sku: [] });
    const [selectedOption, setSelectedOption] = useState(null);

    // States for product metadata
    const [productMetadata, setProductMetadata] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedMetadata, setSelectedMetadata] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    // States for runs and summaries
    const [runSummaries, setRunSummaries] = useState([]);
    const [refreshSummary, setRefreshSummary] = useState(false);

    // States for modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState({});
    const [userNote, setUserNote] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [historyMetadataData, setHistoryMetadataData] = useState([]);
    const [isHistoryModalLoading, setIsHistoryModalLoading] = useState(false);
    const [refresh, setRefresh] = useState(false);

    const user = useUserContext();
    const notify = useNotification();

    // Fetch initial data on component load
    useEffect(() => {
        getAudibleOptionData();
        fetchRunSummaries();
    }, [user, refresh]);

    //  refresh every 10 seconds
    useEffect(() => {

        const getAlerts = () => {
            fetchRunSummaries();
        }

        getAlerts()
        const interval = setInterval(() => getAlerts(), 10000)
        return () => {
            clearInterval(interval);
        }
    }, []);

    // Fetch run summaries
    useEffect(() => {
        fetchRunSummaries();
    }, [refreshSummary, user]);

    // Fetch all university and sku
    const getAudibleOptionData = async () => {
        setLoading(true);
        try {
            // Replace with your actual API call
            const response = await fetchAudibleOptions(user);
            const data = response.data.obj;

            setSearchOptions({
                university: data.university || [],
                sku: data.sku_name || []
            });

            setLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            setLoading(false);
        }
    };

    // Fetch run summaries
    const fetchRunSummaries = async () => {
        try {
            // Replace with your actual API call
            const response = await fetchAudibleSummary(user);
            const data = response.data.obj || [];
            setRunSummaries(data);
        } catch (error) {
            console.error("Error fetching run summaries:", error);
        }
    };

    // Handle search type change
    const handleSearchTypeChange = (event) => {
        setSearchType(event.target.value);
        setSelectedOption(null);
        setProductMetadata([]);
        setSelectedMetadata([]);
    };

    const handleOptionSelect = async (event, value) => {
        if (!value) {
            setSelectedOption(null);
            setProductMetadata([]);
            setSelectedMetadata([]);
            setSelectedRows([]);
            return;
        }

        setSelectedOption(value);
        setLoading(true);

        try {
            // Get the associated product metadata directly from our already fetched data
            const metadataArray = searchType === "university"
                ? searchOptions.university[value] || []
                : searchOptions.sku[value] || [];

            setProductMetadata(metadataArray);
            setSelectedMetadata([]);
        } catch (error) {
            console.error("Error setting product metadata:", error);
        } finally {
            setLoading(false);
        }
    };

    // Handle metadata selection
    const handleMetadataSelect = (id) => {
        setSelectedMetadata(prev => {
            if (prev.includes(id)) {
                return prev.filter(item => item !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    // Handle select all
    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedMetadata(productMetadata.map(item => item.product_metadata_id));
        } else {
            setSelectedMetadata([]);
        }
    };

    // Open confirmation modal
    const handleRunOperation = () => {
        if (selectedMetadata.length === 0) {
            alert("Please select at least one product metadata");
            return;
        }

        //  update selecteItems using the index value of selectedItems
        const selectedItems = productMetadata.filter(item =>
            selectedMetadata.includes(item.product_metadata_id)
        );

        setModalData({
            count: selectedItems.length,
            items: selectedItems
        });

        setIsModalOpen(true);
    };

    const handleConfirmOperation = () => {

        var productMetadatas = []
        for (const metadata of selectedMetadata) {

            productMetadatas.push(metadata)
        }

        if (productMetadata.length == 0) {
            return
        }

        const request_data = {
            metadata_ids: productMetadatas,
            userName: user.displayName,
            notes: userNote,
        }

        setIsSubmitting(true)
        postAudibleArchiveByPMIds(user, request_data)
            .then((response) => {
                notify({
                    msg: "Archive operation successful",
                    variant: "success",
                });
                setIsSubmitting(false)
                setRefreshSummary(!refreshSummary);
                setSelectedMetadata([]);
                setSelectedRows([]);
                setIsModalOpen(false);
                setUserNote("");
                setRefresh(!refresh);
            })
            .catch((error) => {
                setIsSubmitting(false)
                notify("Archive operation failed");
            });

    }

    // 
    const handleAudibleHistoryRowClick = (row) => {

        const requestBody = {}
        setIsHistoryModalLoading(true);
        setIsHistoryModalOpen(true);
        // if (row.metadata_id && row.metadata_id != "") {
        //     requestBody["archive_type"] = "PRODUCTMETADATA"
        //     requestBody["data"] = row.metadata_id.toString()
        // } else if (row.product_ids && row.product_ids != "") {
        //     requestBody["archive_type"] = "PRODUCT"
        //     requestBody["data"] = row.product_ids.toString()
        // } else {
        //     requestBody["archive_type"] = "ATHLETE"
        //     requestBody["data"] = row.roster_id.toString()
        // }

        // getAudibleArchiveHistoryMetadata(user, requestBody).then((response) => {
        //     const data = response.data.obj;
        //     setIsHistoryModalLoading(false);
        //     setHistoryMetadataData(data);
        // }).catch((error) => {
        //     console.error("Error fetching run summaries metadata:", error);
        //     notify("Error fetching run summaries metadata:" + error)
        //     historyModalhandleClose();
        // })

        setIsHistoryModalLoading(false);
        setHistoryMetadataData(row["history_data"]);

    }

    const historyModalhandleClose = () => {
        setIsHistoryModalOpen(false);
        setIsHistoryModalLoading(false);
        setHistoryMetadataData([]);
    };

    return (
        <Container>
            <Typography variant="h4" gutterBottom>
                Product Archive
            </Typography>

            {/* Search Options */}
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Search Options
                </Typography>

                <FormControl component="fieldset">
                    <RadioGroup
                        row
                        name="search-type"
                        value={searchType}
                        onChange={handleSearchTypeChange}
                    >
                        <FormControlLabel
                            value="university"
                            control={<Radio />}
                            label="Search by University"
                        />
                        <FormControlLabel
                            value="sku"
                            control={<Radio />}
                            label="Search by SKU"
                        />
                    </RadioGroup>
                </FormControl>

                <Box sx={{ mt: 2 }}>
                    <Autocomplete
                        options={Object.keys(searchType === "university" ? searchOptions.university : searchOptions.sku)}
                        value={selectedOption}
                        onChange={handleOptionSelect}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={searchType === "university" ? "Select University" : "Select SKU"}
                                variant="outlined"
                            />
                        )}
                        fullWidth
                    />
                </Box>
            </Paper>

            {/* Product Metadata Table */}
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">
                        Product Metadata
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={selectedMetadata.length === 0}
                        onClick={handleRunOperation}
                    >
                        Run Operation
                    </Button>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : productMetadata.length > 0 ? (
                    <ArchiveProductMetadataTable
                        productMetadata={productMetadata}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        setSelectedProductMetadata={setSelectedMetadata} />
                ) : (
                    <Typography variant="body1" sx={{ textAlign: 'center', p: 3 }}>
                        {selectedOption ? "No product metadata found" : "Please select a university or SKU"}
                    </Typography>
                )}
            </Paper>

            {/* Run Summaries */}
            <ArchiveHistoryTable
                runSummaries={runSummaries}
                setRefreshSummary={setRefreshSummary}
                handleRowClick={handleAudibleHistoryRowClick} />

            {/* Confirmation Modal */}
            <Dialog
                open={isModalOpen}
                onClose={() => !isSubmitting && setIsModalOpen(false)}
            >
                <DialogTitle>Confirm Audible Operation</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        You are about to run an operation on {modalData.count} selected product metadata items.
                    </DialogContentText>

                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2">Selected items:</Typography>
                        <TableContainer sx={{ maxHeight: 300 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>University</TableCell>
                                        <TableCell>Sport</TableCell>
                                        <TableCell>Athlete ID</TableCell>
                                        <TableCell>SKU</TableCell>
                                        <TableCell>Type</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {modalData.items?.map((item) => (
                                        <TableRow key={item.product_metadata_id}>
                                            <TableCell>{item.university}</TableCell>
                                            <TableCell>{item.sport}</TableCell>
                                            <TableCell>{item.athlete_id}</TableCell>
                                            <TableCell>{item.sku_name}</TableCell>
                                            <TableCell>{item.type}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                    <TextField
                        margin="dense"
                        label="Operation Note"
                        fullWidth
                        multiline
                        rows={3}
                        value={userNote}
                        onChange={(e) => setUserNote(e.target.value)}
                        disabled={isSubmitting}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setIsModalOpen(false)}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmOperation}
                        color="primary"
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                    >
                        {isSubmitting ? "Processing..." : "Confirm"}
                    </Button>
                </DialogActions>

            </Dialog>
            <ArchiveHistoryMetadataModal open={isHistoryModalOpen} handleClose={historyModalhandleClose} data={historyMetadataData} isLoading={isHistoryModalLoading} />
        </Container>
    );
};

export default ArchiveComponent;